import React, { useEffect, useState, useMemo } from "react";
import "./AdminProjectWeek.css";
import bfLogo from "../assets/bf_logo.png";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download, ArrowLeft, Banknote, User } from "lucide-react";

/* ===== PDF BIBLIOTEKOS ===== */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* ===== FIREBASE ===== */
import { db, auth } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

const DAYS = ["Mo","Di","Mi","Do","Fr","Sa","So"];
const DAYS_MAP = { Mo: "monday", Di: "tuesday", Mi: "wednesday", Do: "thursday", Fr: "friday", Sa: "saturday", So: "sunday" };
const PER_PAGE = 10;

const parseNumber = (value) => {
  if (!value) return 0;
  const str = value.toString().replace(',', '.');
  const match = str.match(/-?\d+(\.\d+)?/); 
  return match ? parseFloat(match[0]) : 0;
};

const latinize = (str) => { 
  if (!str) return ""; 
  return str
    .replace(/[ąĄ]/g, 'a').replace(/[čČ]/g, 'c').replace(/[ęĘ]/g, 'e')
    .replace(/[ėĖ]/g, 'e').replace(/[įĮ]/g, 'i').replace(/[šŠ]/g, 's')
    .replace(/[ųŲ]/g, 'u').replace(/[ūŪ]/g, 'u').replace(/[žŽ]/g, 'z')
    .replace(/[üÜ]/g, 'ue').replace(/[äÄ]/g, 'ae').replace(/[öÖ]/g, 'oe')
    .replace(/[ß]/g, 'ss');
};

const getWeekRange = (kw, yearInput) => {
  if (!kw) return "";
  const year = yearInput || 2026;
  const simple = new Date(year, 0, 1 + (kw - 1) * 7);
  const ISOweekStart = simple;
  const monday = new Date(ISOweekStart);
  monday.setDate(ISOweekStart.getDate() - (ISOweekStart.getDay() === 0 ? 6 : ISOweekStart.getDay() - 1));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d) => `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth()+1).padStart(2, '0')}.${d.getFullYear()}`;
  return `${fmt(monday)} – ${fmt(sunday)}`;
};

const getDayDate = (kw, yearInput, dayIndex) => {
  if (!kw) return "";
  const year = yearInput || 2026;
  const simple = new Date(year, 0, 1 + (kw - 1) * 7);
  const monday = new Date(simple);
  monday.setDate(simple.getDate() - (simple.getDay() === 0 ? 6 : simple.getDay() - 1));
  const targetDate = new Date(monday);
  targetDate.setDate(monday.getDate() + dayIndex);
  return `${String(targetDate.getDate()).padStart(2, '0')}.${String(targetDate.getMonth()+1).padStart(2, '0')}`;
};

export default function AdminProjectWeek() {
  const navigate = useNavigate();
  const { projectId, kw } = useParams();
  const { t } = useTranslation();

  const [project, setProject] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  
  const [workerDetails, setWorkerDetails] = useState({});
  const [realTimeData, setRealTimeData] = useState({});
  
  const [budgetDetails, setBudgetDetails] = useState([]); 

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const uSnap = await getDoc(doc(db, "users", user.uid));
        if (uSnap.exists()) setUserData(uSnap.data());
      } else navigate("/admin/login");
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!projectId || !kw || !currentUser) return;
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, "projects", projectId));
        if (!snap.exists()) return;
        const data = snap.data();
        setProject(data);
        setWorkerDetails(data.workerDetails || {});
        if (data.info?.budgetDetails) setBudgetDetails(data.info.budgetDetails);

        const temp = [];
        const uniqueWorkerIds = new Set();

        Object.entries(data.days || {}).forEach(([day, workerList]) => {
          if (!DAYS.includes(day)) return;
          workerList.forEach(w => {
            let wId = (typeof w === 'object' && w !== null) ? w.id : w;
            if (!wId || wId === "N/A") return;
            uniqueWorkerIds.add(wId); 

            let row = temp.find(r => r.workerId === wId);
            if (!row) {
              row = { workerId: wId, name: w.name || "—", days: {} };
              temp.push(row);
            }
            row.days[day] = true;
          });
        });
        setRows(temp.sort((a, b) => a.name.localeCompare(b.name)));

        const year = data.year || 2026;
        const simpleDate = new Date(year, 0, 1 + (data.kw - 1) * 7);
        const month = (simpleDate.getMonth() + 1).toString().padStart(2, '0');
        
        const detailsMap = {};
        await Promise.all(Array.from(uniqueWorkerIds).map(async (wid) => {
            const docId = `${wid}_${year}_${month}`;
            try {
              const dSnap = await getDoc(doc(db, "monthlyWorkHours", docId));
              if (dSnap.exists()) {
                detailsMap[wid] = dSnap.data().days || {};
              }
            } catch (e) {
              console.error("Error fetching worker details:", e);
            }
        }));
        setRealTimeData(detailsMap);

      } catch (err) { console.error(err); }
    };
    load();
  }, [projectId, kw, currentUser]);

  const getDayNum = (dayIndex) => {
      if (!project) return "1";
      const year = project.year || 2026;
      const simple = new Date(year, 0, 1 + (project.kw - 1) * 7);
      const dayOfWeek = simple.getDay() || 7; 
      simple.setDate(simple.getDate() + 4 - dayOfWeek - 3); 
      const target = new Date(simple);
      target.setDate(simple.getDate() + dayIndex);
      return target.getDate().toString();
  };

  // --- PATAISYTA FUNKCIJA: IEŠKO PAGAL PROJECT ID ---
  const getDisplayValue = (workerId, fieldKey) => {
    const workerDays = realTimeData[workerId];
    // Jei dar nėra duomenų, grąžinam default iš projekto nustatymų
    if (!workerDays) return workerDetails[workerId]?.[fieldKey] || project?.info?.[fieldKey === 'accommodation' ? 'unterkunft' : fieldKey] || "-";

    const grouped = {}; 
    let hasData = false;

    DAYS.forEach((dKey, idx) => {
      const dayList = project?.days?.[dKey] || [];
      const isActive = dayList.some(w => (typeof w === 'string' ? w === workerId : w.id === workerId));
      
      if (isActive) {
        hasData = true;
        const dayNum = getDayNum(idx);
        let dbKey = fieldKey === 'accommodation' ? 'hotel' : fieldKey;
        
        const dayObj = workerDays[dayNum] || {};

        // 1. Pirmiausia ieškome konkretaus projekto stalčiuje (NAUJA LOGIKA)
        let val = dayObj[projectId]?.[dbKey];

        // 2. Jei neradome, bandome seną variantą (tiesiai dienoje) - BACKWARD COMPATIBILITY
        if (!val && typeof dayObj[dbKey] === 'string') {
            val = dayObj[dbKey];
        }

        // 3. Jei vis dar nėra, imam darbuotojo default šiam projektui
        if (!val) val = workerDetails[workerId]?.[fieldKey];

        // 4. Jei vis dar nėra, imam bendrą projekto info
        if (!val) val = project?.info?.[fieldKey === 'accommodation' ? 'unterkunft' : (fieldKey === 'comment' ? 'kommentar' : fieldKey)]; 
        
        if (!val) val = "";

        if (val) {
          if (!grouped[val]) grouped[val] = [];
          grouped[val].push(dKey);
        }
      }
    });

    if (!hasData) return "-";
    const entries = Object.entries(grouped);
    if (entries.length === 0) return "-";

    return entries.map(([val, days]) => {
      if (days.length === 7) return val;
      const translatedDays = days.map(d => t(`days.${d}`));
      return `${val} (${translatedDays.join(", ")})`;
    }).join("; ");
  };

  const totalBudget = useMemo(() => budgetDetails.reduce((acc, item) => acc + parseNumber(item.sum), 0), [budgetDetails]);
  const weekDateRange = project ? getWeekRange(project.kw, project.year) : "";

  const handleDownloadPDF = () => {
    const doc = new jsPDF('landscape');
    
    doc.setFontSize(22);
    doc.setTextColor(0, 0, 0);
    doc.text(latinize(userData?.companyName || "BuildForce"), 14, 15);
    
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    const addr = userData?.address;
    const displayAddr = typeof addr === 'object' ? `${addr.street || ''} ${addr.houseNumber || ''}, ${addr.zip || ''} ${addr.city || ''}` : (addr || "");
    const infoLine = [displayAddr, userData?.email, userData?.phone].filter(Boolean).join(" | ");
    doc.text(latinize(infoLine), 14, 21);
    
    doc.setDrawColor(0, 255, 136); 
    doc.setLineWidth(0.5);
    doc.line(14, 24, 280, 24);

    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(latinize(`${t('weekly_plan').toUpperCase()} - KW ${project.kw}`), 14, 32);
    doc.setFontSize(10);
    doc.text(`Zeitraum: ${weekDateRange}`, 14, 37);

    autoTable(doc, {
      startY: 42,
      margin: { right: 150 },
      head: [[latinize(t('project_info'))]],
      body: [
        [`${latinize(t('project'))}: ${latinize(project.info?.bauvorhaben)}`],
        [`${latinize(t('client'))}: ${latinize(project.info?.auftraggeber)}`],
        [`${latinize(t('address'))}: ${latinize(project.info?.adresse)}`],
        [`${latinize(t('contact_person'))}: ${latinize(project.info?.ansprechpartner)}`],
        [`${latinize(t('contact'))}: ${latinize(project.info?.kontakt)}`]
      ],
      theme: 'grid',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [40, 40, 40] }
    });

    const logData = rows.map(r => {
        return [
            latinize(r.name), 
            latinize(getDisplayValue(r.workerId, 'accommodation')), 
            latinize(getDisplayValue(r.workerId, 'phone')),
            latinize(getDisplayValue(r.workerId, 'transport')),
            latinize(getDisplayValue(r.workerId, 'comment'))
        ];
    });

    autoTable(doc, {
      startY: 42,
      margin: { left: 150 },
      head: [[latinize(t('worker')), latinize(t('accommodation')), latinize(t('phone')), latinize(t('transport')), latinize(t('comment'))]],
      body: logData,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [40, 40, 40] },
      columnStyles: { 4: { cellWidth: 40 } }
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [[latinize(t('worker')), ...DAYS.map((d, i) => `${d} (${getDayDate(project.kw, project.year, i)})`)]],
      body: rows.map(r => [latinize(r.name), ...DAYS.map(d => r.days[d] ? "X" : "-")]),
      theme: 'grid',
      headStyles: { fillColor: [22, 27, 36] },
      styles: { fontSize: 8, halign: 'center' },
      columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } }
    });

    if (budgetDetails.length > 0) {
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 10,
          margin: { left: 160 },
          head: [[latinize(t('description')), "Menge", "Preis", "Summe"]],
          body: [
            ...budgetDetails.map(b => [latinize(b.desc), b.quantity, b.price, `${b.sum} EUR`]),
            [{ content: `${latinize(t('total'))}:`, colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } }, { content: `${totalBudget.toFixed(2)} EUR`, styles: { fontStyle: 'bold', textColor: [0, 150, 0] } }]
          ],
          theme: 'grid',
          headStyles: { fillColor: [255, 204, 0], textColor: [0, 0, 0] }
        });
    }

    doc.save(`Wochenplan_KW${project.kw}.pdf`);
  };

  const visibleRows = useMemo(() => rows.slice((page - 1) * PER_PAGE, (page - 1) * PER_PAGE + PER_PAGE), [rows, page]);

  if (!project) return null;

  return (
    <div className="week-container">
      <style>{`
        /* 1. PAGRINDINIS PUSLAPIO SLANKIKLIS (NEMATOMAS) */
        .week-container {
            height: 100vh;
            display: flex;
            flex-direction: column;
            overflow: hidden; /* Visas langas nejuda */
        }
        .week-header-row {
            flex: 0 0 auto; /* Headeris fiksuotas */
        }
        .week-content {
            flex: 1; /* Užima likusią vietą */
            overflow-y: auto; /* Slankiojasi tik ši dalis */
            /* Paslepia slankiklį */
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
        .week-content::-webkit-scrollbar {
            display: none;
        }

        /* 2. KORTELĖS STILIUS */
        .logistics-card {
            background: #1e1e1e;
            border: 1px solid #333;
            border-radius: 8px;
            margin-bottom: 12px;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            /* Maksimalus aukštis kortelei */
            max-height: 200px; 
        }
        .log-card-header {
            background: rgba(0, 255, 136, 0.1);
            padding: 8px 15px;
            border-bottom: 1px solid #333;
            display: flex;
            align-items: center;
            flex-shrink: 0; /* Headeris nesusispaudžia */
        }
        .worker-name-title {
            color: #00ff88;
            font-weight: 700;
            font-size: 16px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            display: flex;
            align-items: center;
            gap: 10px;
        }

        /* 3. KORTELĖS VIDINIO TURINIO SLANKIKLIS (NEMATOMAS) */
        .log-card-body {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 10px;
            padding: 10px 15px;
            overflow-y: auto; /* Jei netelpa - slankiojasi */
            /* Paslepia slankiklį */
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
        .log-card-body::-webkit-scrollbar {
            display: none;
        }

        .log-detail-item {
            display: flex;
            flex-direction: column;
        }
        .log-label-new {
            font-size: 11px;
            color: #888;
            text-transform: uppercase;
            margin-bottom: 2px;
            font-weight: 600;
        }
        .log-val-new {
            font-size: 13px;
            color: #e0e0e0;
            white-space: pre-wrap;
            line-height: 1.3;
        }
      `}</style>

      <div className="week-header-row no-print">
          <button className="nav-btn back" onClick={() => navigate(-1)}>
              <ArrowLeft size={16} /> {t('back')}
          </button>
      </div>

      <div className="week-content">
        <img src={bfLogo} className="week-logo no-print" alt="BF" />
        <h1 className="week-title no-print">{project.info?.bauvorhaben || t('project')}</h1>
        <div className="week-subtitle no-print">{t('kw')} {project.kw} <div className="week-saved-date">{weekDateRange}</div></div>
        
        <div className="pdf-action-row no-print">
            <button className="bf-green-pdf-btn" onClick={handleDownloadPDF}>
                <Download size={18} /> <span>PDF DOWNLOAD</span>
            </button>
        </div>

        <div className="week-info-panel">
            <div className="info-column general">
                <h3>{t('project_info')}</h3>
                <div className="display-list">
                    <div className="display-item"><span className="label">{t('project')}:</span> <span className="val">{project.info?.bauvorhaben}</span></div>
                    <div className="display-item"><span className="label">{t('client')}:</span> <span className="val">{project.info?.auftraggeber}</span></div>
                    <div className="display-item"><span className="label">{t('address')}:</span> <span className="val">{project.info?.adresse}</span></div>
                    <div className="display-item"><span className="label">{t('contact_person')}:</span> <span className="val">{project.info?.ansprechpartner}</span></div>
                    <div className="display-item"><span className="label">{t('contact')}:</span> <span className="val">{project.info?.kontakt}</span></div>
                </div>
            </div>
            
            <div className="info-column logistics">
                <h3>{t('worker_logistics')}</h3>
                <div className="logistics-list">
                    {rows.map(row => {
                        return (
                            <div key={row.workerId} className="logistics-card">
                                <div className="log-card-header">
                                    <span className="worker-name-title">
                                        <User size={18} /> {row.name}
                                    </span>
                                </div>
                                
                                <div className="log-card-body">
                                    <div className="log-detail-item">
                                        <span className="log-label-new">{t('accommodation')}:</span>
                                        <span className="log-val-new">{getDisplayValue(row.workerId, 'accommodation')}</span>
                                    </div>
                                    <div className="log-detail-item">
                                        <span className="log-label-new">{t('transport')}:</span>
                                        <span className="log-val-new">{getDisplayValue(row.workerId, 'transport')}</span>
                                    </div>
                                    <div className="log-detail-item">
                                        <span className="log-label-new">{t('phone')}:</span>
                                        <span className="log-val-new">{getDisplayValue(row.workerId, 'phone')}</span>
                                    </div>
                                    <div className="log-detail-item">
                                        <span className="log-label-new">{t('comment')}:</span>
                                        <span className="log-val-new">{getDisplayValue(row.workerId, 'comment')}</span>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>

        <div className="week-table-wrapper">
          <table className="week-table">
            <thead>
              <tr>
                <th>{t('worker')}</th>
                {DAYS.map((d, i) => <th key={d}>{t(DAYS_MAP[d])}<div>{getDayDate(project.kw, project.year, i)}</div></th>)}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(w => (
                <tr key={w.workerId}>
                  <td className="worker-name"><span style={{fontWeight:'bold'}}>{w.name}</span></td>
                  {DAYS.map(d => <td key={d}>{w.days[d] ? <span className="check-mark">✓</span> : null}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {budgetDetails.length > 0 && (
            <div className="budget-section-bottom">
                <div className="budget-panel">
                    <h3 className="budget-section-title"><Banknote size={18} /> {t('project_budget')}</h3>
                    <div className="budget-view-table">
                        <div className="bv-header">
                            <span>{t('description')}</span>
                            <span>Menge</span>
                            <span>Preis</span>
                            <span style={{textAlign: 'right'}}>Summe</span>
                        </div>
                        {budgetDetails.map((item, idx) => (
                            <div key={idx} className="bv-row">
                                <span>{item.desc || "-"}</span>
                                <span>{item.quantity || "-"}</span>
                                <span>{item.price || "-"}</span>
                                <span style={{textAlign: 'right', fontWeight: 'bold'}}>{item.sum} €</span>
                            </div>
                        ))}
                        <div className="bv-footer"><span>GESAMT:</span><span className="bv-total-sum">{totalBudget.toFixed(2)} €</span></div>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
}